/** Amount helpers shared by the P-GK invoice list and its summary tiles. */

/** Only the fields the money math needs — both the list row type and tests fit. */
export interface InvoiceAmounts {
  amount_net: string | null;
  amount_gross: string;
  /** "" for companies; "uz" / "uod" for natural-person settlements. */
  mandate_type?: "" | "uz" | "uod";
  status: string;
  paid_amount: string;
}

// Company invoices are compared/discussed net (VAT is pass-through); UZ/UoD
// settlements of natural persons keep the gross contract amount.
export function isMandateInvoice(inv: InvoiceAmounts): boolean {
  return inv.mandate_type === "uz" || inv.mandate_type === "uod";
}

export function displayAmount(inv: InvoiceAmounts): string {
  return isMandateInvoice(inv) ? inv.amount_gross : inv.amount_net ?? inv.amount_gross;
}

/** Rejected ("Nieprzyjęta") and withdrawn ("Wycofana") invoices are dropped from
 *  the summary tiles — neither will ever be paid. Invoices still awaiting the
 *  director ("Przesłana") stay in, by explicit business decision. */
export function countsTowardsTotals(inv: InvoiceAmounts): boolean {
  return inv.status !== "rejected" && inv.status !== "withdrawn";
}

/** Net-based totals for the summary tiles. Payments are recorded gross, so the
 *  paid/outstanding net split is proportional: paid fraction = wpłaty / brutto,
 *  applied to the invoice's display (net; UZ/UoD — contract) amount. */
export function netTotals(invoices: InvoiceAmounts[]) {
  let value = 0;
  let paid = 0;
  let outstanding = 0;
  let count = 0;
  for (const inv of invoices) {
    if (!countsTowardsTotals(inv)) continue;
    const amount = parseFloat(displayAmount(inv));
    const gross = parseFloat(inv.amount_gross);
    const fraction = gross > 0 ? Math.min(1, parseFloat(inv.paid_amount) / gross) : 0;
    count += 1;
    value += amount;
    paid += amount * fraction;
    outstanding += amount * (1 - fraction);
  }
  return { value, paid, outstanding, count };
}
